/**
 * Public-site markdown renderers (code blocks, callouts, headings, tables, lists ...) - ported verbatim
 * from the Trinity_01 reference DocContent.jsx. DocContent switches to these when DocVariantContext is on
 * (the parsing / component dispatch stays this app's own).
 */
import { useState } from 'react';
import { Copy, Check, Terminal, FileCode, Info, Lightbulb, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { DocImage } from '../Media';

const codeTheme = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    background: '#0f172a',
    margin: 0,
    padding: '1rem 1.25rem',
    fontSize: '0.875rem',
    lineHeight: '1.7',
  },
};

const LANG_NAMES = {
  js: 'JavaScript', javascript: 'JavaScript', ts: 'TypeScript', typescript: 'TypeScript',
  jsx: 'JSX', tsx: 'TSX', py: 'Python', python: 'Python',
  bash: 'Bash', sh: 'Shell', shell: 'Shell', json: 'JSON', yaml: 'YAML', yml: 'YAML',
  css: 'CSS', html: 'HTML', sql: 'SQL', go: 'Go', rust: 'Rust', java: 'Java',
  ruby: 'Ruby', php: 'PHP', swift: 'Swift', kotlin: 'Kotlin',
  graphql: 'GraphQL', dockerfile: 'Dockerfile', markdown: 'Markdown', md: 'Markdown',
  xml: 'XML', toml: 'TOML',
};

const CALLOUT_CONFIG = {
  NOTE: { icon: Info, bg: 'bg-slate-500/10', border: 'border-slate-500/20', iconColor: 'text-slate-400', title: 'Note' },
  INFO: { icon: Info, bg: 'bg-blue-500/10', border: 'border-blue-500/30', iconColor: 'text-blue-400', title: 'Info' },
  CHECK: { icon: CheckCircle, bg: 'bg-[#006BE8]/10', border: 'border-[#006BE8]/30', iconColor: 'text-[#006BE8]', title: 'Check' },
  TIP: { icon: Lightbulb, bg: 'bg-[#006BE8]/10', border: 'border-[#006BE8]/30', iconColor: 'text-[#006BE8]', title: 'Tip' },
  WARNING: { icon: AlertTriangle, bg: 'bg-amber-500/10', border: 'border-amber-500/30', iconColor: 'text-amber-400', title: 'Warning' },
  CAUTION: { icon: AlertTriangle, bg: 'bg-amber-500/10', border: 'border-amber-500/30', iconColor: 'text-amber-400', title: 'Caution' },
  ERROR: { icon: AlertCircle, bg: 'bg-red-500/10', border: 'border-red-500/30', iconColor: 'text-red-400', title: 'Error' },
  DANGER: { icon: AlertCircle, bg: 'bg-red-500/10', border: 'border-red-500/30', iconColor: 'text-red-400', title: 'Danger' },
  SUCCESS: { icon: CheckCircle, bg: 'bg-[#006BE8]/10', border: 'border-[#006BE8]/30', iconColor: 'text-[#006BE8]', title: 'Success' },
};

export const KbCodeBlockRenderer = ({ children, className }) => {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : '';
  
  // Extract plain text content from children (handles strings, arrays, and React elements)
  const extractText = (c) => {
    if (c == null) return '';
    if (typeof c === 'string') return c;
    if (Array.isArray(c)) return c.map(extractText).join('');
    if (c.props?.children) return extractText(c.props.children);
    return '';
  };
  
  const code = extractText(children).replace(/\n$/, '');
  
  // Don't render empty or whitespace-only code blocks
  if (!code.trim()) return null;
  
  const langName = LANG_NAMES[language] || language?.toUpperCase() || 'CODE';
  const isTerminal = ['bash', 'sh', 'shell', 'zsh'].includes(language);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(code); } catch (e) { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // No language specified: render as simple pre block without container
  if (!match) {
    return (
      <pre className="my-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-slate-900 text-sm text-gray-700 dark:text-[#999999] overflow-x-auto whitespace-pre-wrap break-words" data-testid="code-block-plain">
        <code>{children}</code>
      </pre>
    );
  }

  return (
    <div className="code-block my-4 rounded-lg overflow-hidden border border-gray-200 dark:border-slate-800" data-testid="code-block">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center gap-2 text-gray-500 dark:text-[#999999]">
          {isTerminal ? <Terminal className="w-4 h-4" /> : <FileCode className="w-4 h-4" />}
          <span className="text-xs font-medium">{langName}</span>
        </div>
        <button onClick={handleCopy} className="flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 dark:text-[#999999] hover:text-gray-900 dark:hover:text-white rounded transition-colors" aria-label={copied ? 'Code copied' : 'Copy code to clipboard'} data-testid="copy-code-btn">
          {copied ? <><Check className="w-3.5 h-3.5 text-[#006BE8]" /><span className="text-[#006BE8]">Copied</span></> : <><Copy className="w-3.5 h-3.5" /><span>Copy</span></>}
        </button>
      </div>
      <SyntaxHighlighter language={language} style={codeTheme} customStyle={{ margin: 0, borderRadius: 0 }}>
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

export const KbCallout = ({ type, title, children }) => {
  const config = CALLOUT_CONFIG[type?.toUpperCase()] || CALLOUT_CONFIG.NOTE;
  const Icon = config.icon;
  const displayTitle = title || config.title;

  return (
    <div className={`my-6 p-4 rounded-lg border relative z-10 ${config.bg} ${config.border} callout-container`} data-testid="callout">
      <div className="flex gap-3">
        <div className={`flex-shrink-0 mt-0.5 ${config.iconColor}`}><Icon className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          {displayTitle && <p className={`font-semibold ${config.iconColor} mb-1 mt-0`}>{displayTitle}</p>}
          <div className="text-[15px] leading-relaxed text-gray-700 dark:text-[#999999] callout-body">{children}</div>
        </div>
      </div>
    </div>
  );
};

// GitHub-style blockquote callouts (> [!NOTE] ...) -> <Callout type="NOTE"> components (reference preprocessing)
export const kbPreprocessCallouts = (content) => {
  if (!content) return content;
  // Handle both escaped \[!TYPE\] and unescaped [!TYPE] brackets, with body on same or next lines
  return content.replace(
    /^>\s*\\?\[!(NOTE|INFO|TIP|WARNING|CAUTION|ERROR|DANGER|SUCCESS)\\?\][ \t]*(.*(?:\\\n.*)*)\n?((?:>.*\n?)*)/gim,
    (match, type, sameLine, rest) => {
      // Clean the same-line body text (remove trailing backslash continuations)
      let body = (sameLine || '').replace(/\\$/gm, '').trim();
      // Clean the subsequent > lines
      const restBody = (rest || '').replace(/^>\s?/gm, '').replace(/\\$/gm, '').trim();
      if (restBody) body = body ? `${body}\n${restBody}` : restBody;
      return `<Callout type="${type}">\n${body}\n</Callout>\n`;
    }
  );
};

const slugify = (children) => String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const kbMdComponents = {
  code: ({ node, inline, className, children, ...props }) => {
    if (inline) return <code className="px-1.5 py-0.5 bg-[#006BE8]/10 dark:bg-[#006BE8]/15 text-[#006BE8] dark:text-[#4D9AFF] rounded text-[0.875em] font-mono" {...props}>{children}</code>;
    return <KbCodeBlockRenderer className={className}>{children}</KbCodeBlockRenderer>;
  },
  pre: ({ children }) => {
    // Let CodeBlockRenderer handle its own container - skip the default pre wrapper
    return <>{children}</>;
  },
  h1: ({ children }) => { const id = slugify(children); return <h1 id={id} className="scroll-mt-20 !text-gray-900 dark:!text-white font-bold" tabIndex={-1}>{children}</h1>; },
  h2: ({ children }) => { const id = slugify(children); return <h2 id={id} className="scroll-mt-20 !text-gray-900 dark:!text-white text-2xl font-semibold mt-10 mb-4" tabIndex={-1}>{children}</h2>; },
  h3: ({ children }) => { const id = slugify(children); return <h3 id={id} className="scroll-mt-20 !text-gray-900 dark:!text-white text-xl font-semibold mt-8 mb-3" tabIndex={-1}>{children}</h3>; },
  h4: ({ children }) => { const id = slugify(children); return <h4 id={id} className="scroll-mt-20 !text-gray-900 dark:!text-white text-lg font-semibold mt-6 mb-2" tabIndex={-1}>{children}</h4>; },
  blockquote: ({ children }) => {
    return <blockquote className="lead-quote my-6 pl-4 border-l-4 border-[#006BE8] italic [&>*]:!text-gray-600 dark:[&>*]:!text-[#999999] [&_p]:!text-gray-600 dark:[&_p]:!text-[#999999]">{children}</blockquote>;
  },
  table: ({ children }) => <div className="overflow-x-auto overflow-y-hidden my-4 rounded-lg border border-gray-200 dark:border-slate-800 max-w-full"><table className="w-full border-collapse min-w-[400px] !mt-0 !mb-0 [&_tr:last-child_td]:border-b-0">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-gray-50 dark:bg-slate-900">{children}</thead>,
  th: ({ children }) => <th className="text-left px-4 py-2.5 text-sm font-semibold !text-gray-900 dark:!text-white border-b border-gray-200 dark:border-slate-800 whitespace-nowrap">{children}</th>,
  td: ({ children }) => <td className="px-4 py-2.5 text-sm !text-gray-700 dark:!text-[#999999] border-b border-gray-100 dark:border-slate-800/50 break-words">{children}</td>,
  a: ({ href, children }) => { const ext = href?.startsWith('http'); return <a href={href} target={ext ? '_blank' : undefined} rel={ext ? 'noopener noreferrer' : undefined} className="text-[#006BE8] hover:text-[#1F7BFF] underline-offset-2 hover:underline">{children}</a>; },
  img: ({ src, alt }) => <DocImage src={src} alt={alt || 'Documentation image'} />,
  hr: () => <hr className="border-gray-200 dark:border-slate-800 my-8" />,
  ul: ({ children }) => <ul className="my-4 ml-6 list-disc space-y-2 !text-gray-700 dark:!text-[#999999]">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 ml-6 list-decimal space-y-2 !text-gray-700 dark:!text-[#999999]">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  p: ({ children }) => <p className="my-4 leading-relaxed !text-gray-700 dark:!text-[#999999]">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold !text-gray-900 dark:!text-white">{children}</strong>,
};
